import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import "../styles/Sidebar.css";
import { Slider } from "@mui/material";
import {
  changeValues,
  updateColor,
  updateRange,
  updateSpeed,
  updateAlgorithm,
} from "../../../redux/slices/visualSlice";
function Sidebar({ updateCurrentStep }) {
  const myState = useSelector((state) => state.updateProps);
  const dispatch = useDispatch();

  const [max, setMax] = useState(30);
  const [algorithm, setAlgorithm] = useState("bubble");

  const handleAlgo = (algo) => {
    setAlgorithm(algo);
    dispatch(updateAlgorithm(algo));
  };

  const resetColor = () => {
    dispatch(updateColor(document.getElementById("color").value));
  };

  const handleRange = (value) => {
    for (let i = 0; i < myState.values.length; i++) {
      const el = document.getElementById(i);
      if (el) el.style.transform = `translateX(${i * 11}px)`;
    }

    resetColor();
    dispatch(updateRange(value));
  };

  const handleColor = (value) => {
    dispatch(updateColor(value));
  };

  const handleSpeed = (value) => {
    dispatch(updateSpeed(value));
  };

  useEffect(() => {
    dispatch(updateRange(30));
  }, []);

  useEffect(() => {
    dispatch(updateColor(document.getElementById("color").value));
  }, [myState.values]);

  const handleWidth = () => {
    if (window.innerWidth > 1300) setMax(70);
    else if (window.innerWidth > 1200) setMax(60);
    else if (window.innerWidth > 1100) setMax(50);
    else if (window.innerWidth > 900) setMax(45);
    else if (window.innerWidth > 800) setMax(40);
    else if (window.innerWidth > 500) setMax(30);
    else setMax(20);
  };

  useEffect(() => {
    handleWidth();
    window.addEventListener("resize", handleWidth);
    return () => window.removeEventListener("resize", handleWidth);
  }, []);

  const algorithms = {
    bubble: [
      "procedure bubbleSort(A : list of sortable items)",
      "    n := length(A)",
      "    repeat",
      "        swapped := false",
      "        for i := 1 to n-1 inclusive do",
      "            if A[i-1] > A[i] then",
      "                swap(A[i-1], A[i])",
      "                swapped := true",
      "            end if",
      "        end for",
      "        n := n - 1",
      "    until not swapped",
      "end procedure",
    ],
    merge: [
      "procedure mergeSort(A : list of sortable items, left : int, right : int)",
      "    if left < right then",
      "        middle := (left + right) / 2",
      "        mergeSort(A, left, middle)",
      "        mergeSort(A, middle + 1, right)",
      "        merge(A, left, middle, right)",
      "    end if",
      "end procedure",
      "",
      "procedure merge(A : list of sortable items, left : int, middle : int, right : int)",
      "    n1 := middle - left + 1",
      "    n2 := right - middle",
      "    let L[1..n1] and R[1..n2] be new arrays",
      "    for i := 1 to n1 do",
      "        L[i] := A[left + i - 1]",
      "    for j := 1 to n2 do",
      "        R[j] := A[middle + j]",
      "    i := 1, j := 1, k := left",
      "    while i ≤ n1 and j ≤ n2 do",
      "        if L[i] ≤ R[j] then",
      "            A[k] := L[i]",
      "            i := i + 1",
      "        else",
      "            A[k] := R[j]",
      "            j := j + 1",
      "        k := k + 1",
      "    while i ≤ n1 do",
      "        A[k] := L[i]",
      "        i := i + 1",
      "        k := k + 1",
      "    while j ≤ n2 do",
      "        A[k] := R[j]",
      "        j := j + 1",
      "        k := k + 1",
      "end procedure",
    ],
    insertion: [
      "procedure insertionSort(A : list of sortable items)",
      "    n := length(A)",
      "    for i := 1 to n - 1 do",
      "        key := A[i]",
      "        j := i - 1",
      "        while j ≥ 0 and A[j] > key do",
      "            A[j + 1] := A[j]",
      "            j := j - 1",
      "        end while",
      "        A[j + 1] := key",
      "    end for",
      "end procedure",
    ],
    selection: [
      "procedure selectionSort(A : list of sortable items)",
      "    n := length(A)",
      "    for i := 0 to n - 1 do",
      "        minIndex := i",
      "        for j := i + 1 to n - 1 do",
      "            if A[j] < A[minIndex] then",
      "                minIndex := j",
      "            end if",
      "        end for",
      "        if minIndex ≠ i then",
      "            swap(A[i], A[minIndex])",
      "        end if",
      "    end for",
      "end procedure",
    ],
    quick: [
      "procedure quickSort(A : list of sortable items, low : int, high : int)",
      "    if low < high then",
      "        pivotIndex := partition(A, low, high)",
      "        quickSort(A, low, pivotIndex - 1)",
      "        quickSort(A, pivotIndex + 1, high)",
      "    end if",
      "end procedure",
      "",
      "procedure partition(A : list of sortable items, low : int, high : int)",
      "    pivot := A[high]",
      "    i := low - 1",
      "    for j := low to high - 1 do",
      "        if A[j] ≤ pivot then",
      "            i := i + 1",
      "            swap(A[i], A[j])",
      "        end if",
      "    end for",
      "    swap(A[i + 1], A[high])",
      "    return i + 1",
      "end procedure",
    ],
    radix: [
      "procedure radixSort(A : list of integers)",
      "    max := getMax(A)",
      "    for exp := 1 to max/10 do",
      "        countingSort(A, exp)",
      "    end for",
      "end procedure",
      "",
      "procedure countingSort(A : list of integers, exp : int)",
      "    n := length(A)",
      "    output[n] := new array",
      "    count[10] := new array initialized to 0",
      "",
      "    for i := 0 to n - 1 do",
      "        index := (A[i] / exp) mod 10",
      "        count[index] := count[index] + 1",
      "    end for",
      "",
      "    for i := 1 to 9 do",
      "        count[i] := count[i] + count[i - 1]",
      "    end for",
      "",
      "    for i := n - 1 to 0 do",
      "        index := (A[i] / exp) mod 10",
      "        output[count[index] - 1] := A[i]",
      "        count[index] := count[index] - 1",
      "    end for",
      "",
      "    for i := 0 to n - 1 do",
      "        A[i] := output[i]",
      "    end for",
      "end procedure",
    ],
    bucket: [
      "procedure bucketSort(A : list of floats in range [0, 1))",
      "    n := length(A)",
      "    B[n] := new list of empty lists",
      "",
      "    for i := 0 to n - 1 do",
      "        insert A[i] into B[floor(n * A[i])]",
      "    end for",
      "",
      "    for i := 0 to n - 1 do",
      "        sort list B[i] using insertionSort",
      "    end for",
      "",
      "    concatenate lists B[0], B[1], ..., B[n-1] together in order",
      "end procedure",
    ],
    heap: [
      "procedure heapSort(A : list of sortable items)",
      "    n := length(A)",
      "",
      "    for i := n / 2 - 1 to 0 do",
      "        heapify(A, n, i)",
      "    end for",
      "",
      "    for i := n - 1 to 0 do",
      "        swap(A[0], A[i])",
      "        heapify(A, i, 0)",
      "    end for",
      "end procedure",
      "",
      "procedure heapify(A : list of sortable items, n : int, i : int)",
      "    largest := i",
      "    left := 2 * i + 1",
      "    right := 2 * i + 2",
      "",
      "    if left < n and A[left] > A[largest] then",
      "        largest := left",
      "    end if",
      "",
      "    if right < n and A[right] > A[largest] then",
      "        largest := right",
      "    end if",
      "",
      "    if largest ≠ i then",
      "        swap(A[i], A[largest])",
      "        heapify(A, n, largest)",
      "    end if",
      "end procedure",
    ],
  };

  return (
    <div className="Ssidebar">
      <div className="Ssidebar__option">
        <label htmlFor="algo">Algorithm: </label>
        <select
          name="algo"
          id="algo"
          onChange={(e) => handleAlgo(e.target.value)}
          disabled={myState.play}
        >
          <option value="bubble">Bubble Sort</option>
          <option value="merge">Merge Sort</option>
          <option value="insertion">Insertion Sort</option>
          <option value="selection">Selection Sort</option>
          <option value="quick">Quick Sort</option>
          <option value="radix">Radix Sort</option>
          <option value="bucket">Bucket Sort</option>
          <option value="heap">Heap Sort</option>
        </select>
      </div>

      <div className="Ssidebar__option">
        <label htmlFor="range">Range: </label>
        <Slider
          style={{ width: "180px" }}
          size="small"
          defaultValue={30}
          id="slider"
          min={1}
          className="slider"
          disabled={myState.play}
          max={max}
          onChange={(e, value) => handleRange(value)}
          valueLabelDisplay="auto"
        />
      </div>

      <div className="Ssidebar__option">
        <label htmlFor="color">Color: </label>
        <select
          name="color"
          id="color"
          onChange={(e) => handleColor(e.target.value)}
          disabled={myState.play}
        >
          <option
            value="rgb(0, 149, 199)"
            style={{ color: "rgb(0, 149, 199)" }}
          >
            Blue
          </option>
          <option value="rgb(85, 212, 0)" style={{ color: "rgb(10,200,20)" }}>
            Green
          </option>
          <option value="rgb(255, 112, 112)" style={{ color: "red" }}>
            Red
          </option>
          <option value="grey" style={{ color: "grey" }}>
            Black
          </option>
          <option value="#ddd902" style={{ color: "#ddd902" }}>
            Yellow
          </option>
        </select>
      </div>

      <div className="Ssidebar__option">
        <label htmlFor="speed">Speed: </label>
        <select
          name="speed"
          defaultValue={100}
          id="speed"
          onChange={(e) => handleSpeed(parseInt(e.target.value))}
          disabled={myState.play}
        >
          <option value={500}>Slow</option>
          <option value={200}>Medium</option>
          <option value={100}>Fast</option>
          <option value={20}>Super Fast</option>
          <option value={5}>Ultra Fast</option>
        </select>
      </div>

      <div className="algorithm-display">
        <h3>
          Algorithm: {algorithm.charAt(0).toUpperCase() + algorithm.slice(1)}{" "}
          Sort
        </h3>
        <pre>
          {algorithms[algorithm].map((line, index) => (
            <code
              key={index}
              className={index === updateCurrentStep ? "highlighted" : ""}
            >
              {line}
            </code>
          ))}
        </pre>
      </div>
    </div>
  );
}

export default Sidebar;
